/**
 * GapTime MDXEditor Plugin
 *
 * Renders epoch timestamps as readable dates in the user's timezone.
 * Click on the component to open a date/time picker and modify the value.
 *
 * MARKDOWN SYNTAX (textDirective for inline use):
 * :GapTime{timestamp="1768953555769"}                    - shows date only
 * :GapTime{timestamp="1768953555769" includeTime="true"} - shows date and time
 *
 * This will render as a formatted date like "Jan 21, 2026" or "Jan 21, 2026, 3:45 PM"
 */

import { DirectiveDescriptor, DirectiveEditorProps, usePublisher, insertDirective$, useMdastNodeUpdater, useCellValue, readOnly$ } from '@mdxeditor/editor'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import Tooltip from '@mui/material/Tooltip'
import { themeConfig } from '@gap/shared/theme'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import DateUtils from '@gap/utils/date-utils'
import { useEffect, useState } from 'react'

const DIRECTIVE_NAME = 'GapTime'

function formatTimestamp(timestamp: string | undefined, includeTime: boolean): string {
   if (!timestamp) {
      return 'Pick a date...'
   }

   const epochMs = parseInt(timestamp, 10)
   if (isNaN(epochMs)) {
      return 'Pick a date...'
   }

   const date = new Date(epochMs)
   if (isNaN(date.getTime())) {
      return 'Invalid date'
   }

   if (includeTime) {
      return date.toLocaleString(undefined, {
         year: 'numeric',
         month: 'short',
         day: 'numeric',
         hour: 'numeric',
         minute: '2-digit',
         hour12: true,
      })
   }

   return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
   })
}

function parseTimestamp(timestamp: string | undefined): Date | null {
   if (!timestamp) return null
   const epochMs = parseInt(timestamp, 10)
   if (isNaN(epochMs)) return null
   const date = new Date(epochMs)
   if (isNaN(date.getTime())) return null
   return date
}

const hiddenTextFieldSlotProps = {
   textField: {
      sx: {
         position: 'absolute',
         width: 0,
         height: 0,
         overflow: 'hidden',
         visibility: 'hidden',
      },
   },
} as const

function GapTimeComponent({ mdastNode }: DirectiveEditorProps) {
   const [isOpen, setIsOpen] = useState(false)
   const updateMdastNode = useMdastNodeUpdater()
   const isReadOnly = useCellValue(readOnly$)

   const attributes = mdastNode.attributes as Record<string, string> | undefined
   const timestamp = attributes?.timestamp
   const includeTime = attributes?.includeTime === 'true'
   const formattedDate = formatTimestamp(timestamp, includeTime)
   const dateValue = parseTimestamp(timestamp)

   // Auto-open picker when inserted with empty timestamp ("Later" variant)
   useEffect(() => {
      if (!timestamp && !isReadOnly) {
         setIsOpen(true)
      }
   }, []) // eslint-disable-line react-hooks/exhaustive-deps

   const handleOpen = () => {
      if (!isReadOnly) {
         setIsOpen(true)
      }
   }
   const handleClose = () => setIsOpen(false)

   const handleDateChange = (newValue: Date | null) => {
      if (newValue && !isNaN(newValue.getTime())) {
         updateMdastNode({
            attributes: {
               ...attributes,
               timestamp: String(newValue.getTime()),
            },
         })
      }
   }

   const Icon = includeTime ? AccessTimeIcon : CalendarTodayIcon

   if (isReadOnly) {
      return `${formattedDate}`
   }

   return (
      <Box component="span" sx={{ display: 'inline-block', position: 'relative' }}>
         <Box
            component="span"
            onClick={handleOpen}
            sx={{
               display: 'inline-flex',
               alignItems: 'center',
               gap: 0.5,
               backgroundColor: themeConfig.primary[50],
               border: `1px solid ${themeConfig.primary[200]}`,
               borderRadius: 1,
               px: 1,
               py: 0.25,
               cursor: 'pointer',
               '&:hover': {
                  backgroundColor: themeConfig.primary[100],
                  borderColor: themeConfig.primary[300],
               },
            }}
         >
            <Icon sx={{ fontSize: 14, color: themeConfig.primary[500] }} />
            <Typography
               component="span"
               variant="caption"
               sx={{
                  color: themeConfig.primary[700],
                  fontWeight: 500,
               }}
            >
               {formattedDate}
            </Typography>
         </Box>
         {includeTime ? (
            <DateTimePicker
               open={isOpen}
               onOpen={handleOpen}
               onClose={handleClose}
               value={dateValue}
               onChange={handleDateChange}
               format={DateUtils.DATE_TIME_FORMAT}
               timeSteps={{ minutes: 15 }}
               slotProps={hiddenTextFieldSlotProps}
            />
         ) : (
            <DatePicker
               open={isOpen}
               onOpen={handleOpen}
               onClose={handleClose}
               value={dateValue}
               onChange={handleDateChange}
               format={DateUtils.DATE_FORMAT}
               slotProps={hiddenTextFieldSlotProps}
            />
         )}
      </Box>
   )
}

export const GapTimeDirectiveDescriptor: DirectiveDescriptor = {
   name: DIRECTIVE_NAME,
   type: 'textDirective',
   testNode(node) {
      return node.name === DIRECTIVE_NAME
   },
   attributes: ['timestamp', 'includeTime'],
   hasChildren: false,
   Editor: GapTimeComponent,
}

export function InsertGapTime() {
   const insertDirective = usePublisher(insertDirective$)

   const handleInsertClick = () => {
      const now = Date.now()
      insertDirective({
         name: DIRECTIVE_NAME,
         type: 'textDirective',
         attributes: { timestamp: String(now) },
      })
   }

   return (
      <Tooltip title="Insert current timestamp">
         <IconButton size="small" onClick={handleInsertClick}>
            <AccessTimeIcon fontSize="small" />
         </IconButton>
      </Tooltip>
   )
}
